// ---- Per-map terrain context (plan 21) ----
// A SELF-CONTAINED heightAt/surfaceAt/inWater/clampToShore for ONE map doc —
// the multi-map server's answer to the module-level terrain state (layers.ts
// setMapTerrain + flatten.ts zones + active.ts water level), which can only
// point at one map at a time. Each MapRt (src/server/maps.ts) captures a
// TerrainCtx at boot; server runtime code resolves ground through it, NEVER
// through the module-level heightAt (which holds whichever map hydrated last).
//
// Composition is the SAME math as terrain.ts heightAt:
//   noise(offset) → features (pre-carve) → carveShore(waterLevel) → brush →
//   flats + structure pads
// pinned equal by src/map/terrain-ctx.test.ts (the migration-parity test's
// successor: it compares this composition against the live module heightAt).

import type { MapDoc } from "../content/schemas.ts";
import { noiseBase, carveShore } from "../terrain.ts";
import { hydrateFeatures, foldFeatures, hydrateBrush, brushDelta } from "./layers.ts";
import { applyFlattenTo, type FlatZone } from "../flatten.ts";
import { WATER_LEVEL_DEFAULT } from "./active.ts";
import {
  surfaceAtWith, clampToShoreWith, standAtWith, slopeGateWith, wadeClampWith, airWallGateWith,
  type Surface,
} from "../physics.ts";

export type TerrainCtx = {
  heightAt(x: number, z: number): number;
  /** surfaceAt over THIS map's ground (surfaces = the map's walkable floors). */
  surfaceAt(x: number, z: number, prevY: number, surfaces: Surface[]): number;
  /** standAtWith over THIS map's ground: null = cliff face (gravity plan). */
  standAt(x: number, z: number, refY: number, surfaces: Surface[]): number | null;
  inWater(x: number, z: number): boolean;
  clampToShore(fromX: number, fromZ: number, x: number, z: number): { x: number; z: number };
  /** Steep-terrain climb gate over THIS map's ground (gravity plan). */
  slopeGate(fromX: number, fromZ: number, x: number, z: number, maxSlope: number): { x: number; z: number };
  /** Wading clamp (already-in-water mover — only shoreward moves survive). */
  wadeClamp(fromX: number, fromZ: number, x: number, z: number): { x: number; z: number };
  /** Airborne cliff-column gate (an arc can't steer inside a hill). */
  airWallGate(fromX: number, fromZ: number, x: number, z: number, feetY: number): { x: number; z: number };
  waterLevel: number;
};

/** Build a map's TerrainCtx. `structureZones` = the flatten pads buildWorld
 *  derived for this map's placements (world.ts buildStructurePlacements) —
 *  capture them right after the map's headless build, while flatten.ts's
 *  module state still holds them. */
export function makeTerrainCtx(doc: MapDoc, structureZones: FlatZone[]): TerrainCtx {
  const features = hydrateFeatures(doc.terrain.features);
  const brush = hydrateBrush(doc.terrain.brush);
  const [ox, oz] = doc.terrain.noiseOffset ?? [0, 0];
  const amp = doc.terrain.amp ?? 1;
  const zones: FlatZone[] = [...(doc.terrain.flats as FlatZone[]), ...structureZones];
  const waterLevel = doc.env?.waterLevel ?? WATER_LEVEL_DEFAULT;
  const stop = waterLevel + 0.15; // the shoreStop() rule against THIS map's level

  function heightAt(x: number, z: number): number {
    const base = foldFeatures(x, z, noiseBase(x, z, ox, oz, amp), features);
    const h = carveShore(base, waterLevel) + brushDelta(x, z, brush);
    return applyFlattenTo(x, z, h, zones);
  }

  return {
    heightAt,
    surfaceAt: (x, z, prevY, surfaces) => surfaceAtWith(heightAt, x, z, prevY, surfaces),
    standAt: (x, z, refY, surfaces) => standAtWith(heightAt, x, z, refY, surfaces),
    inWater: (x, z) => heightAt(x, z) < stop,
    clampToShore: (fromX, fromZ, x, z) => clampToShoreWith(heightAt, waterLevel, fromX, fromZ, x, z),
    slopeGate: (fromX, fromZ, x, z, maxSlope) => slopeGateWith(heightAt, fromX, fromZ, x, z, maxSlope),
    wadeClamp: (fromX, fromZ, x, z) => wadeClampWith(heightAt, waterLevel, fromX, fromZ, x, z),
    airWallGate: (fromX, fromZ, x, z, feetY) => airWallGateWith(heightAt, fromX, fromZ, x, z, feetY),
    waterLevel,
  };
}
